import { useQuery } from '@tanstack/react-query';
import { Visit } from '../types/visit';

interface UseVisitsOptions {
  date?: Date;
  type?: 'regular' | 'outreach';
  status?: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  patientId?: string;
  reason?: string;
}

// Mock data function - replace with actual API call
async function fetchVisits(options: UseVisitsOptions): Promise<Visit[]> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));

  // Mock data
  const visits: Visit[] = [
    {
      id: '1',
      patientId: '1',
      clinicId: '1',
      doctorId: '1',
      startTime: '2024-03-20T09:00:00',
      endTime: '2024-03-20T09:30:00',
      duration: 30,
      type: 'regular',
      status: 'scheduled',
      reason: 'Regular Check-up',
      patient: {
        id: '1',
        firstName: 'John',
        lastName: 'Doe',
        dateOfBirth: new Date('1990-01-01'),
        gender: 'male',
        email: 'john.doe@example.com',
        phone: '123-456-7890',
        address: {
          street: '123 Main St',
          city: 'Anytown',
          state: 'ST',
          zipCode: '12345',
        },
        insuranceInfo: {
          provider: 'Insurance Co',
          policyNumber: '12345',
          groupNumber: '67890',
        },
        medicalHistory: "",
        allergies: "",
        medications: [],
        status: 'active',
      },
      notes: 'Patient reports feeling better after last visit',
      diagnosis: 'Common cold with mild fever',
      prescriptions: [
        {
          medication: 'Paracetamol',
          dosage: '500mg',
          frequency: 'twice daily',
          duration: '5 days',
          notes: 'Take after meals'
        },
        {
          medication: 'Vitamin C',
          dosage: '1000mg',
          frequency: 'twice daily',
          duration: '5 days',
          notes: 'Take after meals'
        }
      ],
      symptoms: ['Fever', 'Cough', 'Fatigue'],
      vitals: {
        temperature: 37.8,
        bloodPressure: '120/80',
        heartRate: 75,
        respiratoryRate: 16
      },
      createdAt: '2024-03-20T09:00:00',
      updatedAt: '2024-03-20T09:00:00'
    },
    {
      id: '2',
      patientId: '2',
      clinicId: '1',
      doctorId: '1',
      startTime: '2024-03-20T10:00:00',
      endTime: '2024-03-20T10:30:00',
      duration: 30,
      type: 'outreach',
      status: 'in-progress',
      reason: 'Follow-up',
      patient: {
        id: '2',
        firstName: 'Jane',
        lastName: 'Smith',
        dateOfBirth: new Date('1985-05-15'),
        gender: 'female',
        email: 'jane.smith@example.com',
        phone: '234-567-8901',
        address: {
          street: '456 Oak St',
          city: 'Somewhere',
          state: 'ST',
          zipCode: '67890',
        },
        insuranceInfo: {
          provider: 'Insurance Co',
          policyNumber: '67890',
          groupNumber: '12345',
        },
        medicalHistory: "",
        allergies: "",
        medications: [],
        status: 'active',
      },
      notes: 'Follow-up visit for chronic condition management',
      diagnosis: 'Hypertension - well controlled',
      prescriptions: [
        {
          medication: 'Amlodipine',
          dosage: '5mg',
          frequency: 'once daily',
          duration: '30 days',
          notes: 'Take in the morning'
        }
      ],
      symptoms: ['Occasional headache'],
      vitals: {
        temperature: 36.9,
        bloodPressure: '130/85',
        heartRate: 72,
        respiratoryRate: 14
      },
      createdAt: '2024-03-20T10:00:00',
      updatedAt: '2024-03-20T10:00:00'
    },
  ];

  // Filter visits based on options
  let filteredVisits = visits;
  
  if (options.patientId) {
    filteredVisits = filteredVisits.filter(visit => visit.patientId === options.patientId);
  }
  if (options.type) {
    filteredVisits = filteredVisits.filter(visit => visit.type === options.type);
  }
  if (options.status) {
    filteredVisits = filteredVisits.filter(visit => visit.status === options.status);
  }
  if (options.reason) {
    filteredVisits = filteredVisits.filter(visit => visit.reason === options.reason);
  }

  // Generate titles dynamically based on reason and patient name
  return filteredVisits.map(visit => ({
    ...visit,
    title: `${visit.reason} - ${visit.patient?.firstName} ${visit.patient?.lastName}`
  }));
}

export function useVisits(options: UseVisitsOptions = {}) {
  return useQuery({
    queryKey: ['visits', options],
    queryFn: () => fetchVisits(options),
  });
}
